<template>
        <v-row dense>
            <v-col cols="12" class="mb-3">
                <v-card elevation="3" :loading="loading">
                <v-card-title class="text-subtitle-1 font-weight-bold">
                    Deadlines of Submissions
                    <v-divider class="my-2"></v-divider>
                </v-card-title>
                <v-card-text>
                    <v-row class="d-flex">
                        <v-col v-for="d in deadlinesData" :key="d" class="d-flex flex-column flex-md-row justify-start align-left align-lg-center ga-2">
                            <div v-if="d.term_id != null"><b>{{ d.term.name }}</b></div>
                            <v-chip>
                                Deadline: &nbsp; <b>{{ formatDate(d.deadline) }}</b>
                            </v-chip>
                            <v-chip v-if="d.is_extended == true">
                                Deadline Extension: &nbsp; <b>{{ formatDate(d.deadline_extension) }}</b>
                            </v-chip>
                        </v-col>
                    </v-row>
                </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" md="4">
                <v-text-field
                    v-model="search"
                    variant="outlined"
                    prepend-inner-icon="mdi-magnify"
                    label="Search"
                    density="comfortable"
                    solo
                    hide-details
                    rounded="lg"
                    color="primary"
                ></v-text-field>
            </v-col>
            <v-col v-if="compliance.is_term_separated" class="d-flex flex-column flex-md-row justify-end ga-2" cols="12" md="8">
                <v-btn
                    v-for="btn in compliance.schedules" :key="btn"
                    @click="showSubmitDialogWithTerm(btn)"
                    prepend-icon="mdi-upload"
                    class="mt-1 rounded-lg"
                    color="routesColor"
                    height="40px"
                    elevation="2">
                    Submit {{ btn.term.name }}
                </v-btn>
            </v-col>
            <v-col v-else class="text-right" cols="12" md="8">
                <v-btn
                @click="showSubmitDialog()"
                prepend-icon="mdi-upload"
                class="mt-1 rounded-lg"
                color="routesColor"
                height="40px"
                elevation="2">
                    Submit Compliance
                </v-btn>
            </v-col>
            <v-col cols="12">
                <v-row dense>
                    <v-col cols="12">
                        <v-card class="dt-container mt-2">    
                            <v-data-table
                                :mobile="isMobileOrTablet()"
                                :headers="!compliance.is_sorted_course ? notSortedHeader : compliance.is_term_separated ? termSeparatedHeaders : headers"
                                :items="data"
                                :search="search"
                                :loading="loading"
                            >
                            <template v-slot:loading>
                                <v-progress-linear indeterminate color="primary" height="5" class="mt-1 rounded-lg"></v-progress-linear>
                            </template>
                            <template v-slot:item.file="{ item }">
                                <v-chip :color="getStatusColor(item)" class="text-caption">
                                    <span>{{ getStatusText(item) }}</span>
                                </v-chip>
                            </template>
                            <template v-slot:item.datecreated="{ item }">
                                <span>{{ formatDateTime(item.datecreated)}} </span>
                            </template>
                            <template v-slot:item.is_printed="{ item }">
                                <v-chip v-if="item.file.is_printed" color="green" size="small"> {{ formatDate(item.file.printed_date) }} <v-icon color="green">mdi-check</v-icon></v-chip>
                                <v-icon v-if="item.file.is_printed == false" color="red">mdi-close</v-icon>
                            </template>
                            <template v-slot:[`item.action`]="{ item }">
                                    <v-btn size="small" @click="showViewCompliance(item)" variant="outlined" text="View" color="green-accent-4" elevation="2"></v-btn>
                            </template>
                            </v-data-table>
                        </v-card>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>

        <ComplianceSubmissionDialog ref="ComplianceSubmissionDialog" @refresh="init(this.compliance)" />
        <ViewSubmittedCompliance ref="ViewSubmittedCompliance" @refresh="init(this.compliance)"/>
</template>

<script>
import axios from 'axios';
import { defineComponent } from 'vue';
import ComplianceSubmissionDialog from '../Dialogs/ComplianceSubmissionDialog.vue';
import ViewSubmittedCompliance from '../Dialogs/ViewSubmittedCompliance.vue';

export default defineComponent({
    components: {
        ComplianceSubmissionDialog,
        ViewSubmittedCompliance
    },
    data(){
        return {
            data: [],
            search: null,
            loading: false,

            headers: [
                { title: 'Course Code', align: 'start', sortable: true, key: 'facultyLoad.code' },
                { title: 'Course Title', align: 'start', sortable: true, key: 'facultyLoad.title' },
                { title: 'Date Submitted', align: 'start', sortable: true, key: 'datecreated' },
                { title: 'Status', align: 'start', sortable: true, key: 'file' },
                { title: 'Actions', align: 'end', sortable: false, key: 'action' },
            ],

            termSeparatedHeaders: [
                { title: 'Course Code', align: 'start', sortable: true, key: 'facultyLoad.code' },
                { title: 'Course Title', align: 'start', sortable: true, key: 'facultyLoad.title' },
                { title: 'Term', align: 'start', sortable: true, key: 'term.name' },
                { title: 'Date Submitted', align: 'start', sortable: true, key: 'datecreated' },
                { title: 'Status', align: 'start', sortable: true, key: 'file' },
                { title: 'Is Printed', align: 'staet', sortable: false, key: 'is_printed' },
                { title: 'Actions', align: 'end', sortable: false, key: 'action' },
            ],

            notSortedHeader: [
                { title: 'Course Code', align: 'start', sortable: true, key: 'facultyLoad.code' },
                { title: 'Course Title', align: 'start', sortable: true, key: 'facultyLoad.title' },
                { title: 'Section Code', align: 'start', sortable: true, key: 'facultyLoad.section_code' },
                { title: 'Date Submitted', align: 'start', sortable: true, key: 'datecreated' },
                { title: 'Status', align: 'start', sortable: true, key: 'file' },
                { title: 'Actions', align: 'end', sortable: false, key: 'action' },
            ],

            compliance: [],
            deadlinesData: [],

            smAndDown: false
        }
    },
    methods: {
        init(data){
           this.compliance = data
           this.deadlinesData = data.schedules

           this.loading = true
            const token = localStorage.getItem('aums-token')
            axios.get(process.env.VUE_APP_SERVER_URL + '/compliance-upload-details/findAllComplianceUpload/' + this.$store.state.user.id + '/' + this.$store.state.semester_schoolyear.id + '/' + this.compliance.id,
            { headers:
                { 
                    'Authorization': `Bearer ${token}`,
                }
            }
            ).then((res)=>{
                if(res.data){
                    this.loading = false
                    this.data = res.data
                }
            }).catch((error)=>{
                if(error.response.status == 401){
                    this.loading = false

                    localStorage.removeItem("vuex");
                    localStorage.removeItem("aums-token");
                    window.location.reload();
                }
                else{
                    console.log(error)
                }
            });
        },
        getStatusText(item) {
            if (!item.file.review_status && !item.file.approval_status) return "For Review";
            if (item.file.review_status === "reviewed" && !item.file.approval_status) return "For Approval";
            if (item.file.review_status === "returned" && !item.file.approval_status) return "Returned by Reviewer";
            if (item.file.review_status === "reviewed" && item.file.approval_status === "returned") return "Returned by Approver";
            if (item.file.review_status === "reviewed" && item.file.approval_status === "approved") return "Approved";
            return "Unknown Status";
        },
        getStatusColor(item) {
            if (!item.file.review_status && !item.file.approval_status) return "orange";
            if (item.file.review_status === "reviewed" && !item.file.approval_status) return "blue";
            if (item.file.review_status === "returned" && !item.file.approval_status) return "red";
            if (item.file.review_status === "reviewed" && item.file.approval_status === "returned") return "deep-orange";
            if (item.file.review_status === "reviewed" && item.file.approval_status === "approved") return "green";
            return "gray";
        },
        showSubmitDialog(){
            this.$refs.ComplianceSubmissionDialog.initAdd(this.compliance)
        },
        showSubmitDialogWithTerm(schedule){
            this.$refs.ComplianceSubmissionDialog.initAddWithTerm(this.compliance, schedule)
        },
        showViewCompliance(data){
            this.$refs.ViewSubmittedCompliance.initView(data)
        },
    },
    mounted(){
        
    }
})
</script>

<style scoped>

</style>